// Operator shell for the Desarrollos tenant: sidebar, section routing, and the
// /desarrollos/anexo/:id path. Every screen underneath reads live Supabase
// data — there is no mock data on this surface.

import { useCallback, useEffect, useState } from 'react';
import {
  LayoutDashboard,
  Users,
  Inbox,
  DollarSign,
  Settings,
  Menu,
  X,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import SivarSendWordmark from '../components/brand/SivarSendWordmark';
import DashboardHome from './operador/DashboardHome';
import ClientesPanel from './operador/ClientesPanel';
import DesarrollosActivosQueue from './operador/DesarrollosActivosQueue';
import DesarrollosAnexoWorkspace from './operador/DesarrollosAnexoWorkspace';
import EnConstruccion from './operador/EnConstruccion';

const SECTIONS = [
  { key: 'anexos', label: 'Anexos activos', icon: Inbox },
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'clientes', label: 'Clientes', icon: Users },
  { key: 'ingresos', label: 'Ingresos', icon: DollarSign },
  { key: 'ajustes', label: 'Ajustes', icon: Settings },
];

const QUEUE_PATH = '/desarrollos/operador/anexos';
const ANEXO_PATH = /^\/desarrollos\/operador\/anexos\/([^/]+)\/?$/;

const SidebarNav = ({ active, onSelect, anexoCount }) => (
  <nav className="flex flex-col gap-1 p-3">
    {SECTIONS.map((s) => {
      const Icon = s.icon;
      const isActive = s.key === active;
      const showBadge = s.key === 'anexos' && anexoCount > 0;
      return (
        <button
          key={s.key}
          type="button"
          onClick={() => onSelect(s.key)}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors ${
            isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
          }`}
        >
          <Icon size={16} />
          <span className="flex-1">{s.label}</span>
          {showBadge && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-900 text-white">
              {anexoCount}
            </span>
          )}
        </button>
      );
    })}
  </nav>
);

const Wordmark = () => <SivarSendWordmark size="sm" />;

const OperadorDashboard = ({ onLogout }) => {
  // The queue is the operator's landing surface for Desarrollos.
  const [active, setActive] = useState('anexos');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [anexoCount, setAnexoCount] = useState(0);
  // Path-based routing without a router dependency, same spirit as App.js's
  // /track/* branch: pushState to navigate, popstate to follow the back button.
  // react-router is NOT a dependency of this project.
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to) => {
    window.history.pushState({}, '', to);
    setPath(to);
  }, []);

  const openAnexoWorkspace = useCallback(
    (id) => {
      navigate(`${QUEUE_PATH}/${id}`);
    },
    [navigate]
  );

  // From the Dashboard list: jump to the queue.
  const revealAnexoInQueue = useCallback(() => {
    setActive('anexos');
  }, []);

  const backToQueue = useCallback(() => {
    navigate(QUEUE_PATH);
    setActive('anexos');
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    // The operator session is a real Supabase session now — end it server-side
    // before handing control back to the login card.
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error('[Desarrollos] error cerrando sesión', err);
    }
    onLogout();
  }, [onLogout]);

  const anexoMatch = path.match(ANEXO_PATH);
  const activeLabel = anexoMatch
    ? 'Anexo'
    : SECTIONS.find((s) => s.key === active)?.label || '';

  const renderSection = () => {
    if (active === 'dashboard') return <DashboardHome onOpenAnexo={revealAnexoInQueue} />;
    if (active === 'clientes') return <ClientesPanel />;
    if (active === 'anexos') {
      return (
        <DesarrollosActivosQueue
          onOpenAnexo={openAnexoWorkspace}
          onCountChange={setAnexoCount}
          onDenied={handleLogout}
        />
      );
    }
    return <EnConstruccion title={activeLabel} />;
  };

  // El workspace es una superficie de tres paneles a pantalla completa: se
  // monta sin la barra lateral, y se sale de él por "← Anexos activos".
  if (anexoMatch) {
    return (
      <DesarrollosAnexoWorkspace
        anexoId={anexoMatch[1]}
        onBack={backToQueue}
        onDenied={handleLogout}
      />
    );
  }

  const selectSection = (key) => {
    if (key === 'anexos') navigate(QUEUE_PATH);
    else if (anexoMatch) navigate(QUEUE_PATH);
    setActive(key);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-56 shrink-0 bg-white border-r border-slate-200">
        <div className="h-14 flex items-center px-5 border-b border-slate-200">
          <Wordmark />
        </div>
        <SidebarNav
          active={anexoMatch ? 'anexos' : active}
          onSelect={selectSection}
          anexoCount={anexoCount}
        />
        <button
          type="button"
          onClick={handleLogout}
          className="mt-auto mx-3 mb-4 text-xs text-slate-400 hover:text-slate-600 text-left px-3 transition-colors"
        >
          Cerrar sesión
        </button>
      </aside>

      {/* Mobile drawer */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setSidebarOpen(false)}>
          <aside className="w-64 h-full bg-white flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 flex items-center justify-between px-5 border-b border-slate-200">
              <Wordmark />
              <button type="button" onClick={() => setSidebarOpen(false)} aria-label="Cerrar menú">
                <X size={18} className="text-slate-400" />
              </button>
            </div>
            <SidebarNav
              active={anexoMatch ? 'anexos' : active}
              onSelect={(k) => {
                selectSection(k);
                setSidebarOpen(false);
              }}
              anexoCount={anexoCount}
            />
            <button
              type="button"
              onClick={handleLogout}
              className="mt-auto mx-3 mb-4 text-xs text-slate-400 hover:text-slate-600 text-left px-3 transition-colors"
            >
              Cerrar sesión
            </button>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <div className="md:hidden h-14 flex items-center gap-3 px-4 bg-white border-b border-slate-200">
          <button type="button" onClick={() => setSidebarOpen(true)} aria-label="Abrir menú">
            <Menu size={20} className="text-slate-600" />
          </button>
          <Wordmark />
        </div>

        <div className="p-5 sm:p-8">
          {/* La cola trae su propio encabezado (título + chip de conteo +
              "Actualizar"), así que el shell no lo duplica. */}
          {!(active === 'anexos' && !anexoMatch) && (
            <h1 className="text-lg font-bold text-slate-900 mb-6">{activeLabel}</h1>
          )}
          {renderSection()}
        </div>
      </div>
    </div>
  );
};

export default OperadorDashboard;
